/**
 * MunicipalityInteractionLog — read-only display of permit log entries.
 *
 * Shows newest-first, marks superseded entries, and groups by day.
 * Pure presentational — append-only invariant lives in
 * `appendMunicipalityInteraction`.
 */
import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Phone, Mail, MessageSquare, MapPin, Voicemail, AlertCircle, Calendar, User, Building2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  type MunicipalityInteraction,
  type MunicipalityInteractionType,
  INTERACTION_TYPE_LABEL,
  isSupersededInteraction,
} from "@/data/permitting/municipalityLog";

const TYPE_ICON: Record<MunicipalityInteractionType, React.ElementType> = {
  call: Phone,
  email: Mail,
  portal_message: MessageSquare,
  in_person: MapPin,
  voicemail: Voicemail,
};

type LogFilter =
  | "all"
  | MunicipalityInteractionType
  | "follow_up_due"
  | "corrections"
  | "homeowner";

const FILTERS: { key: LogFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "homeowner", label: "Homeowner" },
  { key: "call", label: "Calls" },
  { key: "email", label: "Emails" },
  { key: "portal_message", label: "Portal" },
  { key: "in_person", label: "In Person" },
  { key: "voicemail", label: "Voicemail" },
  { key: "follow_up_due", label: "Follow-up Due" },
  { key: "corrections", label: "Corrections" },
];

interface Props {
  log: MunicipalityInteraction[];
  /** Limit visible entries (e.g. mini-feed). Undefined = show all. */
  limit?: number;
  emptyHint?: string;
  /** Show filter chips above the list. Default false to keep mini-feeds clean. */
  showFilters?: boolean;
}

export function MunicipalityInteractionLog({ log, limit, emptyHint, showFilters }: Props) {
  const [filter, setFilter] = useState<LogFilter>("all");
  const now = Date.now();
  const filteredLog = useMemo(() => {
    if (!showFilters || filter === "all") return log;
    return log.filter((entry) => {
      switch (filter) {
        case "homeowner":
          return entry.contactType === "homeowner";
        case "follow_up_due": {
          if (!entry.followUpDate) return false;
          if (isSupersededInteraction(entry, log)) return false;
          const t = Date.parse(entry.followUpDate);
          return !Number.isNaN(t) && t < now;
        }
        case "corrections":
          return !!entry.supersedesId;
        default:
          return entry.type === filter;
      }
    });
  }, [log, filter, showFilters, now]);
  const filterChips = showFilters ? (
    <div className="flex flex-wrap gap-1.5 mb-2" role="group" aria-label="Interaction filters">
      {FILTERS.map((f) => {
        const active = filter === f.key;
        return (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
              active
                ? "bg-primary/15 text-primary ring-1 ring-primary/30 border-transparent"
                : "border-border text-muted-foreground hover:bg-muted/40"
            }`}
          >
            {f.label}
          </button>
        );
      })}
    </div>
  ) : null;

  if (log.length === 0) {
    return (
      <>
        {filterChips}
        <p className="text-xs text-muted-foreground italic">
          {emptyHint ?? "No municipality interactions logged yet."}
        </p>
      </>
    );
  }
  const ordered = [...filteredLog].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const visible = limit ? ordered.slice(0, limit) : ordered;

  if (showFilters && ordered.length === 0) {
    return (
      <>
        {filterChips}
        <p className="text-xs text-muted-foreground italic">
          No interactions match this filter.
        </p>
      </>
    );
  }

  return (
    <>
      {filterChips}
      <ul className="space-y-2">
      {visible.map((entry) => {
        const Icon = TYPE_ICON[entry.type];
        const superseded = isSupersededInteraction(entry, log);
        const corrects = entry.supersedesId
          ? log.find((e) => e.id === entry.supersedesId)
          : null;
        const followUp = entry.followUpDate ? new Date(entry.followUpDate) : null;
        const overdue = followUp && followUp < new Date();
        const isHomeownerEntry = entry.contactType === "homeowner";
        return (
          <li
            key={entry.id}
            className={`rounded-md border p-3 text-sm ${
              superseded
                ? "opacity-50 bg-muted/30"
                : isHomeownerEntry
                  ? "bg-amber-50/40 dark:bg-amber-950/20"
                  : "bg-background"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              {isHomeownerEntry ? (
                <User className="h-3.5 w-3.5 text-warning shrink-0" />
              ) : (
                <>
                  <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <Icon className="h-3.5 w-3.5 text-primary shrink-0" />
                </>
              )}
              <span className="font-medium text-foreground">
                {INTERACTION_TYPE_LABEL[entry.type]}
              </span>
              {isHomeownerEntry && (
                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800">
                  Homeowner
                </Badge>
              )}
              <span className="text-muted-foreground">·</span>
              <span className="text-muted-foreground">{entry.contactPerson}</span>
              <span className="ml-auto text-[11px] text-muted-foreground tabular-nums">
                {format(new Date(entry.createdAt), "MMM d, h:mm a")}
              </span>
            </div>
            <p className="text-foreground whitespace-pre-wrap">{entry.summary}</p>
            {entry.nextStep && (
              <div className="mt-2 text-xs">
                <span className="text-muted-foreground">Next step: </span>
                <span className="text-foreground">{entry.nextStep}</span>
              </div>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
              {followUp && (
                <Badge
                  variant="outline"
                  className={`gap-1 ${overdue ? "border-destructive/40 text-destructive" : ""}`}
                >
                  <Calendar className="h-3 w-3" />
                  Follow-up {format(followUp, "MMM d")}
                </Badge>
              )}
              {entry.municipalityId === "unmapped" && !isHomeownerEntry && (
                <Badge variant="outline" className="gap-1 border-warning/40 text-warning">
                  Unmapped{entry.rawMunicipalityLabel ? ` · ${entry.rawMunicipalityLabel}` : ""}
                </Badge>
              )}
              <span className="text-muted-foreground">Logged by {entry.loggedBy}</span>
              {superseded && (
                <Badge variant="outline" className="gap-1 border-warning/40 text-warning">
                  <AlertCircle className="h-3 w-3" /> Superseded
                </Badge>
              )}
              {corrects && (
                <span className="text-muted-foreground italic">
                  Corrects entry from {format(new Date(corrects.createdAt), "MMM d, h:mm a")}
                </span>
              )}
            </div>
          </li>
        );
      })}
      {limit && ordered.length > limit && (
        <li className="text-[11px] text-muted-foreground text-center pt-1">
          {ordered.length - limit} earlier {ordered.length - limit === 1 ? "entry" : "entries"} not shown
        </li>
      )}
    </ul>
    </>
  );
}
